import { G } from '@mobily/ts-belt';
import RefreshIcon from '@mui/icons-material/Cached';
import InfoIcon from '@mui/icons-material/Info';
import { Box, colors, Tooltip } from '@mui/material';
import { get } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useGetList, useNotify, useRecordContext, useRefresh, useTranslate } from 'react-admin';

import { GetAsyncFlowOneResponse, GetAsyncFlowsQuery } from '@Common/types/bo/async_flows';
import { B2bCampaignStatus, PutB2bCampaignRefreshEndpoint } from '@Common/types/bo/b2b/campaigns';

import { Button } from '@sh4res/bo-common/components';
import { b2bCampaignStatusChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { SelectChipField, SelectChipFieldProps } from '@sh4res/bo-common/fields';
import { useUpdatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { getAsyncFlowTransitionsSummary } from '@sh4res/bo-common/utils/async_flow_transitions';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export function CampaignStatusField({
  ['data-testid']: dataTestId,
  inList = false,
  ...props
}: SelectChipFieldProps & { source: string; inList?: boolean }) {
  const record = useRecordContext(props);
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [refreshCampaign, { isLoading: refreshingCampaign }] = useUpdatePromise<
    Record<string, never>,
    PutB2bCampaignRefreshEndpoint['Client']['ResponseBody']
  >();

  const filter = useMemo<Exclude<GetAsyncFlowsQuery['filter'], undefined>>(
    () => ({ archived: false, flow: 'employee_savings_campaign', b2b_campaign_id: record?.id }),
    [record],
  );

  const { data } = useGetList<GetAsyncFlowOneResponse>(
    'async_flows',
    {
      sort: { field: 'created', order: 'DESC' },
      filter,
    },
    {
      enabled:
        G.isNotNullable(record?.id) &&
        !inList &&
        !isBoExternal() &&
        record?.status?.endsWith('failed'),
    },
  );
  const transitions = data ? getAsyncFlowTransitionsSummary(data) : [];

  const handleCampaignRefresh = useCallback(async () => {
    try {
      if (!record || refreshingCampaign) {
        return;
      }
      await refreshCampaign('b2b/campaigns/refresh', { id: record.id, data: {} });
      refresh();
      notify('resources.b2b/campaigns.actions.refresh.success', {
        type: 'success',
      });
    } catch (error: unknown) {
      notify('resources.b2b/campaigns.actions.refresh.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    }
  }, [record, refreshingCampaign, refreshCampaign, refresh, notify]);

  if (!record) {
    return null;
  }

  const status = get(record, props.source) as B2bCampaignStatus;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SelectChipField
        {...props}
        data-testid={`${dataTestId}_StatusField`}
        sx={{
          ...([
            'campaign_awaiting_customer_confirmation',
            'campaign_pending_allocation_computation',
          ].includes(status) && {
            backgroundColor: colors.orange[500],
            color: 'white',
          }),
        }}
        choices={b2bCampaignStatusChoices}
        emptyText={translate('resources.bo_common.b2b.campaigns.choices.status.campaign_created')}
      />
      {!inList && !isBoExternal() && record.status?.endsWith('failed') && (
        <>
          <Tooltip
            title={
              <>
                {transitions.map((transition) => (
                  <p key={transition}>{transition}</p>
                ))}
              </>
            }>
            <InfoIcon />
          </Tooltip>
          <Button
            disabled={refreshingCampaign}
            onClick={handleCampaignRefresh}
            data-testid={`${dataTestId}_RefreshFailedStatusButton`}
            title={translate('resources.b2b/campaigns.actions.refresh.label')}
            sx={{ '& .MuiButton-startIcon': { ml: '0px', mr: '0px' } }}
            permissions={{ resource: 'b2b/campaigns/refresh', action: 'edit' }}>
            <RefreshIcon />
          </Button>
        </>
      )}
    </Box>
  );
}
